import { SurveyQuestion, SurveyResponse, SurveySubmission, SurveyAnalytics, SurveyConfig } from '../types/PluginTypes';
import { fetchAPI } from '../services/api'; // Import du wrapper API centralisé

// Validation avancée des questions
export const validateQuestionResponse = (question: SurveyQuestion, value: any): { isValid: boolean; message?: string } => {
  if (question.required && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
    return { isValid: false, message: 'Cette question est obligatoire' };
  }

  if (!value || value === '') return { isValid: true };

  // Validation par type
  switch (question.type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return { isValid: false, message: 'Veuillez saisir une adresse email valide' };
      }
      break;

    case 'phone':
      const phoneRegex = /^(\+33|0)[1-9](\d{8})$/;
      if (!phoneRegex.test(value.replace(/\s/g, ''))) {
        return { isValid: false, message: 'Veuillez saisir un numéro de téléphone valide' };
      }
      break;

    case 'url':
      try {
        new URL(value);
      } catch {
        return { isValid: false, message: 'Veuillez saisir une URL valide' };
      }
      break;

    case 'number':
      const num = parseFloat(value);
      if (isNaN(num)) {
        return { isValid: false, message: 'Veuillez saisir un nombre valide' };
      }
      if (question.validation?.min !== undefined && num < question.validation.min) {
        return { isValid: false, message: `La valeur doit être supérieure ou égale à ${question.validation.min}` };
      }
      if (question.validation?.max !== undefined && num > question.validation.max) {
        return { isValid: false, message: `La valeur doit être inférieure ou égale à ${question.validation.max}` };
      }
      break;

    case 'text':
    case 'textarea':
      if (question.validation?.min && value.length < question.validation.min) {
        return { isValid: false, message: `Le texte doit contenir au moins ${question.validation.min} caractères` };
      }
      if (question.validation?.max && value.length > question.validation.max) {
        return { isValid: false, message: `Le texte ne peut pas dépasser ${question.validation.max} caractères` };
      }
      if (question.validation?.pattern) {
        const regex = new RegExp(question.validation.pattern);
        if (!regex.test(value)) {
          return { 
            isValid: false, 
            message: question.validation.customMessage || 'Le format saisi n\'est pas valide' 
          };
        }
      }
      break;

    case 'date':
      if (isNaN(Date.parse(value))) {
        return { isValid: false, message: 'Veuillez saisir une date valide' };
      }
      break;

    case 'scale':
      const scaleValue = parseInt(value);
      if (isNaN(scaleValue)) {
        return { isValid: false, message: 'Veuillez sélectionner une valeur sur l\'échelle' };
      }
      if (question.scaleMin && scaleValue < question.scaleMin) {
        return { isValid: false, message: `La valeur doit être au moins ${question.scaleMin}` };
      }
      if (question.scaleMax && scaleValue > question.scaleMax) {
        return { isValid: false, message: `La valeur ne peut pas dépasser ${question.scaleMax}` };
      }
      break;
  }

  return { isValid: true };
};

// Logique conditionnelle - détermine si une question doit être affichée
export const shouldShowQuestion = (question: SurveyQuestion, responses: { [questionId: string]: any }): boolean => {
  if (!question.conditionalLogic) return true;

  const { dependsOn, showIf, value: conditionValue } = question.conditionalLogic;
  const dependentValue = responses[dependsOn];

  if (dependentValue === undefined || dependentValue === null) return false;

  switch (showIf) {
    case 'equals':
      return dependentValue === conditionValue;
    
    case 'not_equals':
      return dependentValue !== conditionValue;
    
    case 'contains':
      if (typeof dependentValue === 'string') {
        return dependentValue.toLowerCase().includes(String(conditionValue).toLowerCase());
      }
      return false;
    
    case 'greater_than':
      const numValue = parseFloat(dependentValue);
      const numCondition = parseFloat(String(conditionValue));
      return !isNaN(numValue) && !isNaN(numCondition) && numValue > numCondition;
    
    case 'less_than':
      const numValue2 = parseFloat(dependentValue);
      const numCondition2 = parseFloat(String(conditionValue));
      return !isNaN(numValue2) && !isNaN(numCondition2) && numValue2 < numCondition2;
    
    case 'selected':
      if (Array.isArray(dependentValue)) {
        return Array.isArray(conditionValue) 
          ? conditionValue.some(val => dependentValue.includes(val))
          : dependentValue.includes(conditionValue);
      }
      return dependentValue === conditionValue;
    
    case 'not_selected':
      if (Array.isArray(dependentValue)) {
        return Array.isArray(conditionValue) 
          ? !conditionValue.some(val => dependentValue.includes(val))
          : !dependentValue.includes(conditionValue);
      }
      return dependentValue !== conditionValue;
    
    default:
      return true;
  }
};

// Calcul du taux de progression
export const calculateProgress = (questions: SurveyQuestion[], responses: { [questionId: string]: any }): number => {
  const visibleQuestions = questions.filter(q => shouldShowQuestion(q, responses));
  const answeredQuestions = visibleQuestions.filter(q => {
    const response = responses[q.id];
    return response !== undefined && response !== '' && response !== null;
  });
  
  return visibleQuestions.length > 0 ? (answeredQuestions.length / visibleQuestions.length) * 100 : 0;
};

// Petit utilitaire sûr pour convertir n'importe quelle valeur en ISO
function toIso(value: any): string {
  if (!value) return '';
  try {
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? '' : d.toISOString();
    }
    // fallback
    const d = new Date(value as any);
    return isNaN(d.getTime()) ? '' : d.toISOString();
  } catch {
    return '';
  }
}

// Échappement CSV sûr
function csvEscape(value: any): string {
  const s = value === null || value === undefined ? '' : String(value);
  const escaped = s.replace(/"/g, '""');
  return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
}

// Export CSV des résultats
export function exportToCSV(submissions: SurveySubmission[], questions: SurveyQuestion[]): string {
  const headers = [
    'ID Soumission',
    'Date de soumission',
    'Temps total (secondes)',
    'Taux de completion (%)',
    ...questions.map(q => q.title.replace(/,/g, ';'))
  ];

  const rows = submissions.map((submission) => {
    const submittedAtIso = toIso((submission as any).submittedAt);

    const responseMap = submission.responses.reduce((acc, r) => {
      acc[r.questionId] = r.value;
      return acc;
    }, {} as { [key: string]: any });

    return [
      submission.id,
      submittedAtIso,
      String(submission.totalTimeSpent),
      submission.completionRate.toFixed(1),
      ...questions.map(q => {
        const value = responseMap[q.id];
        if (Array.isArray(value)) return csvEscape(value.join('; '));
        return csvEscape(value);
      })
    ];
  });

  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
};

// Export Excel (structure JSON pour conversion)
export const exportToExcel = (submissions: SurveySubmission[], questions: SurveyQuestion[]) => {
  return {
    fileName: `survey_results_${new Date().toISOString().split('T')[0]}.xlsx`,
    sheets: [
      {
        name: 'Responses',
        data: submissions.map(submission => {
          const responseMap = submission.responses.reduce((acc, response) => {
            acc[response.questionId] = response.value;
            return acc;
          }, {} as { [key: string]: any });

          const row: any = {
            'ID': submission.id,
            'Date de soumission': toIso((submission as any).submittedAt),
            'Temps total (s)': submission.totalTimeSpent,
            'Taux de completion (%)': submission.completionRate.toFixed(1)
          };

          questions.forEach(q => {
            const value = responseMap[q.id];
            row[q.title] = Array.isArray(value) ? value.join('; ') : (value ?? '');
          });

          return row;
        })
      },
      {
        name: 'Analytics',
        data: [
          { Métrique: 'Nombre total de soumissions', Valeur: submissions.length },
          { Métrique: 'Temps moyen de completion (s)', Valeur: submissions.reduce((acc, s) => acc + s.totalTimeSpent, 0) / submissions.length || 0 },
          { Métrique: 'Taux de completion moyen (%)', Valeur: (submissions.reduce((acc, s) => acc + s.completionRate, 0) / submissions.length || 0).toFixed(1) }
        ]
      }
    ]
  };
};

// Génération d'analytics
export const generateAnalytics = (submissions: SurveySubmission[], questions: SurveyQuestion[]): SurveyAnalytics => {
  const questionAnalytics: { [questionId: string]: any } = {};

  questions.forEach(question => {
    const responses = submissions.flatMap(s => s.responses.filter(r => r.questionId === question.id));
    const responseValues = responses.map(r => r.value).filter(v => v !== undefined && v !== null && v !== '');
    
    // Distribution des réponses
    const distribution: { [value: string]: number } = {};
    responseValues.forEach(value => {
      const key = Array.isArray(value) ? value.join(', ') : String(value);
      distribution[key] = (distribution[key] || 0) + 1;
    });

    questionAnalytics[question.id] = {
      responseCount: responseValues.length,
      averageTimeSpent: responses.reduce((acc, r) => acc + (r.timeSpent || 0), 0) / responses.length || 0,
      responseDistribution: distribution,
      abandonmentRate: responses.length > 0 ? (1 - (responseValues.length / responses.length)) * 100 : 0
    };
  });

  // ✅ Données temporelles (par jour) — dates sécurisées
  const timeSeriesMap: { [date: string]: number } = {};
  submissions.forEach(submission => {
    const iso = toIso((submission as any).submittedAt);
    if (!iso) return;
    const day = iso.split('T')[0];
    timeSeriesMap[day] = (timeSeriesMap[day] || 0) + 1;
  });

  const timeSeriesData = Object.entries(timeSeriesMap)
    .map(([date, submissions]) => ({ date, submissions }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    totalSubmissions: submissions.length,
    completionRate: submissions.length > 0 
      ? (submissions.reduce((acc, s) => acc + s.completionRate, 0) / submissions.length)
      : 0,
    averageTimeSpent: submissions.length > 0 
      ? (submissions.reduce((acc, s) => acc + s.totalTimeSpent, 0) / submissions.length)
      : 0,
    questionAnalytics,
    timeSeriesData
  };
};

// Protection anti-spam simple
export const isSpamSubmission = (submission: SurveySubmission): boolean => {
  // Vérifications basiques anti-spam
  const suspiciousPatterns = [
    /viagra|casino|lottery|pills/i,
    /^\d+$/, // Que des chiffres
    /(.)\1{10,}/, // Répétition excessive
  ];

  const allText = submission.responses
    .map(r => String(r.value || ''))
    .join(' ')
    .toLowerCase();

  return suspiciousPatterns.some(pattern => pattern.test(allText)) ||
         submission.totalTimeSpent < 5; // Moins de 5 secondes = suspect
};

// Stockage local pour les données survey (pour la demo)
export const SurveyStorage = {
  saveSubmission: async (
    surveyId: string,
    submission: SurveySubmission,
    meta?: { ownerId?: string | number; ownerEmail?: string; projectId?: number }
  ) => {
    try {
      // Appel à la nouvelle route API
      // fetchAPI gère déjà l'URL de base et les headers Auth si dispo
      const response = await fetchAPI(`/surveys/${surveyId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...submission,
          ownerId: meta?.ownerId,
          ownerEmail: meta?.ownerEmail,
          projectId: meta?.projectId
        })
      });
      
      console.log('Réponse sauvegardée (API):', response);
      return response;

    } catch (error) {
      console.error('Erreur lors de la sauvegarde en ligne, repli sur localStorage:', error);
      
      // Fallback : Sauvegarde locale si le serveur échoue (mode hors ligne)
      const key = `survey_submissions_${surveyId}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push({ ...submission, _synced: false }); // Marquer comme non synchronisé
      localStorage.setItem(key, JSON.stringify(existing));
      
      throw error; // On relance l'erreur pour que l'UI sache qu'il y a eu un souci réseau
    }
  },

  getSubmissions: async (surveyId: string): Promise<SurveySubmission[]> => {
    try {
      // Tentative de récupération depuis le serveur (si route stats dispo)
      return await fetchAPI(`/surveys/${surveyId}/stats`);
    } catch (e) {
      // Fallback local
      const key = `survey_submissions_${surveyId}`;
      return JSON.parse(localStorage.getItem(key) || '[]');
    }
  },

  clearSubmissions: (surveyId: string) => {
    const key = `survey_submissions_${surveyId}`;
    localStorage.removeItem(key);
  }
};
